import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';

import { LoggerService } from '@services/logger.service';
import { produce } from 'immer';
import { FormControl, FormGroup } from '@angular/forms';
import { Permission, Principal } from '../../../../core/models/object.models';
import { LoadingComponent } from '../../../../shared/components/loading/loading.component';
import { SuiButtonComponent } from '@sui/button';
import { SuiTooltipDirective } from '@sui/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { FallbackImageDirective } from '../../../../shared/directives/fallback-image.directive';
import { SuiCheckboxComponent } from '@sui/checkbox';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-object-permissions-advanced',
  templateUrl: './object-permissions-advanced.component.html',
  styleUrl: './object-permissions-advanced.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    LoadingComponent,
    SuiButtonComponent,
    SuiTooltipDirective,
    FaIconComponent,
    FallbackImageDirective,
    SuiCheckboxComponent,
    TranslatePipe,
  ],
})
export class ObjectPermissionsAdvancedComponent implements OnInit, OnChanges {
  @Input() permissions: any[] = []; // provided by wrapper
  @Input() principals: any[] = []; // provided by wrapper
  @Input() loading = false;
  @Input() NomObjectPermission: any; // enum, if needed in template
  @Input() formGroup: FormGroup;

  @Output() addPrincipalsRequested = new EventEmitter<void>();
  @Output() removePrincipalRequested = new EventEmitter<any>();

  @Output() permissionToggle = new EventEmitter<{
    permission: any;
    principal: any;
    checked: boolean;
  }>();
  @Output() toggleAllForPrincipal = new EventEmitter<{
    principal: any;
    checked: boolean;
  }>();

  // Build these once (e.g., after you load permissions) and reuse:
  private prereqMap = new Map<number, Set<number>>(); // perm -> prerequisites (linked_permission_id)
  private dependentMap = new Map<number, Set<number>>(); // perm -> dependents (reverse edges)

  // local working copy to render changes immediately
  viewPermissions: Permission[] = [];

  constructor(protected logger: LoggerService) {}

  ngOnInit(): void {
    this.viewPermissions = this.clonePermissions(this.permissions);
    this.syncPermissionControls(); // build initial controls
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['permissions']) {
      this.viewPermissions = this.clonePermissions(this.permissions);
      this.syncPermissionControls(); // rebuild all
    } else if (changes['principals']) {
      this.syncPermissionControls(); // principals added/removed
    }
  }

  private clonePermissions(src: any[]) {
    // shallow clone is usually fine, but keep produce to be safe
    return produce(src ?? [], (draft) => draft);
  }

  hasAllPermissions(principal: any): boolean {
    return (this.viewPermissions ?? []).every((permission: Permission) =>
      permission.principals?.some((p: any) => p.principal_id === principal.id),
    );
  }

  isPermissionChecked(permission: any, principal: any): boolean {
    return permission?.principals?.some(
      (p: any) => p.principal_id === principal.id,
    );
  }

  private buildPermissionGraphs(perms: Permission[]) {
    this.prereqMap.clear();
    this.dependentMap.clear();

    for (const p of perms) {
      const links = Array.isArray(p.linked_permission_id)
        ? p.linked_permission_id
        : [];
      this.prereqMap.set(p.permission_id, new Set(links));
      for (const prereq of links) {
        if (!this.dependentMap.has(prereq))
          this.dependentMap.set(prereq, new Set());
        this.dependentMap.get(prereq)!.add(p.permission_id);
      }
    }
  }

  // Helper to get transitive closure over a map<id, Set<id>>
  private closure(startId: number, map: Map<number, Set<number>>): Set<number> {
    const seen = new Set<number>();
    const q: number[] = [startId];
    while (q.length) {
      const cur = q.shift()!;
      if (seen.has(cur)) continue;
      seen.add(cur);
      const next = map.get(cur);
      if (next) for (const n of next) if (!seen.has(n)) q.push(n);
    }
    return seen;
  }

  handlePermissionClick(
    checked: boolean,
    permission: Permission,
    principal: Principal,
  ) {
    // ensure graphs reflect current list
    this.buildPermissionGraphs(this.viewPermissions);

    const principalId = principal.id;
    const principalClassId = principal.object_class_id;

    // Which permission ids should be affected?
    // - If checking: permission + all its prerequisites (downward via prereqMap)
    // - If unchecking: permission + all its dependents (upward via dependentMap)
    const affectedIds = checked
      ? this.closure(permission.permission_id, this.prereqMap)
      : this.closure(permission.permission_id, this.dependentMap);

    // Always include the toggled permission itself (closure already does, but keep for clarity)
    affectedIds.add(permission.permission_id);

    this.viewPermissions = produce(
      this.viewPermissions,
      (draft: Permission[]) => {
        // quick index for faster lookups
        const byId = new Map<number, Permission>(
          draft.map((p) => [p.permission_id, p]),
        );

        for (const id of affectedIds) {
          const dp = byId.get(id);
          if (!dp) continue;

          if (checked) {
            // add principal if missing
            const exists = dp.principals?.some(
              (p) => p.principal_id === principalId,
            );
            if (!exists) {
              if (!Array.isArray(dp.principals)) dp.principals = [];
              dp.principals.push({
                principal_id: principalId,
                principal_class_id: principalClassId,
              });
            }
          } else {
            // remove principal from this permission
            if (Array.isArray(dp.principals)) {
              dp.principals = dp.principals.filter(
                (p) => p.principal_id !== principalId,
              );
            }
          }
        }
      },
    );

    this.syncPermissionControls();

    this.permissionToggle.emit({ permission, principal, checked });
  }

  toggleAllPrincipalPermissions(checked: boolean, principal: any) {
    if (principal.is_owner) return;

    this.viewPermissions = produce(this.viewPermissions, (draft: any[]) => {
      for (const permission of draft) {
        if (checked) {
          const exists = permission.principals.some(
            (p: any) => p.principal_id === principal.id,
          );
          if (!exists) {
            permission.principals.push({
              principal_id: principal.id,
              principal_class_id: principal.object_class_id,
            });
          }
        } else {
          permission.principals = permission.principals.filter(
            (p: any) => p.principal_id !== principal.id,
          );
        }
      }
    });

    this.syncPermissionControls();

    this.toggleAllForPrincipal.emit({ principal, checked });
  }

  // Call this to (re)build ALL controls, or pass a principal to refresh ONLY theirs.
  private syncPermissionControls(): void {
    const buildKey = (pr: any) => `permission[${pr.object_class_id}][${pr.id}]`;

    const collectSelectedIds = (pr: any): number[] => {
      const selected = new Set<number>();
      for (const perm of this.viewPermissions ?? []) {
        if (perm?.principals?.some((p: any) => p.principal_id === pr.id)) {
          selected.add(perm.permission_id);
        }
      }
      return Array.from(selected);
    };

    // full rebuild: remove stale permission[...] controls

    const liveKeys = new Set(
      (this.principals ?? []).map((pr: any) => buildKey(pr)),
    );

    Object.keys(this.formGroup.controls)
      .filter((k) => k.startsWith('permission[') && !liveKeys.has(k))
      .forEach((k) => this.formGroup.removeControl(k));

    // ensure every principal has an up-to-date control
    for (const pr of this.principals ?? []) {
      const key = buildKey(pr);
      const value = collectSelectedIds(pr);
      if (!this.formGroup.contains(key)) {
        this.formGroup.addControl(
          key,
          new FormControl({ value, disabled: !!pr.is_owner }),
        );
      } else {
        const ctrl = this.formGroup.get([key]) as FormControl;
        ctrl.setValue(value, { emitEvent: false });
        ctrl[pr.is_owner || pr.is_business_admin ? 'disable' : 'enable']({
          emitEvent: false,
        });
      }
    }
  }

  removePrincipal(principal: any) {
    if (principal.is_owner || principal.is_business_admin) return;
    const key = `permission[${principal.object_class_id}][${principal.id}]`;
    if (this.formGroup.contains(key)) this.formGroup.removeControl(key);
    this.removePrincipalRequested.emit(principal);
    //this.rebuildPrincipalControls();
  }

  get colCount(): number {
    return (this.viewPermissions?.length ?? 0) + 1;
  }

  addPrincipals(): void {
    this.addPrincipalsRequested.emit();
  }

  trackByPerm = (_: number, p: any) => p.permission_id;
  trackByPrincipal = (_: number, pr: any) => pr.id;
}
